import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Booking } from '@/types';

interface UseBookingRealtimeOptions {
  bookingId: string | undefined;
  onUpdate: (booking: Booking | null) => void;
  enabled?: boolean;
  pollInterval?: number; // Intervalo para mock mode (default: 2000ms)
}

/**
 * Hook para escutar mudanças em tempo real de uma booking específica
 * - Production mode: Usa Supabase Realtime subscriptions
 * - Mock mode: Usa polling com setInterval
 */
export function useBookingRealtime({
  bookingId,
  onUpdate,
  enabled = true,
  pollInterval = 2000,
}: UseBookingRealtimeOptions) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !bookingId) {
      return;
    }

    // Use Supabase Realtime
    // Verificar se bookingId é um UUID válido (Supabase Realtime precisa do ID real do banco)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(bookingId)) {
      // bookingId is not a valid UUID (probably BK-XXX format), skipping Realtime subscription
      // Will subscribe once booking is loaded and UUID is available
      return;
    }

    // Setting up Realtime subscription for booking
    
    const channel = supabase
      .channel(`booking-${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'bookings',
          filter: `id=eq.${bookingId}`,
        },
        (payload) => {
          console.log('📊 Booking change detected (realtime):', payload.eventType, payload.new || payload.old);
          
          // Para UPDATE, usar payload.new; para DELETE, usar null
          if (payload.eventType === 'DELETE') {
            onUpdate(null);
            return;
          }

          // Para INSERT ou UPDATE, verificar se o status mudou
          const newStatus = (payload.new as any)?.status;
          const previousStatus = lastStatusRef.current;
          
          // Sempre atualizar lastStatusRef para o novo status
          lastStatusRef.current = newStatus;
          
          // Se o status mudou, trigger reload
          if (newStatus !== previousStatus) {
            console.log('📊 Booking status changed (realtime):', previousStatus, '->', newStatus);
            // Trigger reload chamando onUpdate com null para forçar recarregamento completo
            onUpdate(null);
          } else {
            // Mesmo que o status não tenha mudado, pode haver outras mudanças (ex: filmmaker_id)
            // Trigger reload para garantir que temos os dados mais atualizados
            console.log('📊 Booking updated (realtime) - status unchanged, but reloading for other fields');
            onUpdate(null);
          }
        }
      )
      .subscribe((status) => {
        console.log('🔴 Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to booking changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime subscription error');
        }
      });

    channelRef.current = channel;

    return () => {
      console.log('🧹 Cleaning up Realtime subscription');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      lastStatusRef.current = null;
    };
  }, [bookingId, enabled, onUpdate, pollInterval]);
}
